import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../components/layout/Footer';
import classService from '../services/classService';
import { getDisplayDuration, formatClassLocationDisplay, formatClassPrice, localizeClassItem } from '../config/classUiConfig';

// Default image for fallback
const defaultClassImage = 'https://res.cloudinary.com/your-cloud-name/image/upload/v1/yj-childcare-classes/default-class';

function Classes() {
    const { t, i18n } = useTranslation('classes');
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
        fetchClasses();
    }, []);

    const fetchClasses = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await classService.getAllClasses();

            // Reorder classes: CDA to top, CPR to bottom
            const reorderedClasses = [...data];

            // Find CDA and move it to the top
            const cdaIndex = reorderedClasses.findIndex(c =>
                c.title.toLowerCase().includes('cda') ||
                c.title.toLowerCase().includes('child development associate')
            );

            if (cdaIndex !== -1 && cdaIndex !== 0) {
                const cdaClass = reorderedClasses.splice(cdaIndex, 1)[0];
                reorderedClasses.unshift(cdaClass);
            }

            // Find CPR and move it to the bottom
            const cprIndex = reorderedClasses.findIndex(c =>
                c.title.toLowerCase().includes('cpr') ||
                c.title.toLowerCase().includes('first aid')
            );

            if (cprIndex !== -1 && cprIndex !== reorderedClasses.length - 1) {
                const cprClass = reorderedClasses.splice(cprIndex, 1)[0];
                reorderedClasses.push(cprClass);
            }

            setClasses(reorderedClasses);
        } catch (err) {
            setError(err.message || t('loadError'));
        } finally {
            setLoading(false);
        }
    };


    // Helper function to get image source
    const getImageSource = (classItem) => {
        // If the class has a Cloudinary image URL, use it
        if (classItem.image_url) {
            // Add Cloudinary transformations for optimal display
            return classItem.image_url.replace('/upload/', '/upload/c_fill,w_500,h_350,q_auto,f_auto/');
        }
        // Use default image if no image is set
        return defaultClassImage;
    };

    const localizedClasses = useMemo(
        () => classes.map((c) => localizeClassItem(c, i18n.language)),
        [classes, i18n.language]
    );

    const getDurationString = (classItem) => {
        return getDisplayDuration(classItem, i18n.language);
    };

    const renderLoadingState = () => (
        <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
    );

    const renderErrorState = () => (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                <p>{error}</p>
                <button
                    onClick={fetchClasses}
                    className="mt-4 bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200 transition-colors"
                >
                    {t('tryAgain')}
                </button>
            </div>
        </div>
    );


    return (
        <div className="bg-white min-h-screen font-montserrat">

            {/* Hero Section */}
            <section className="relative w-full h-[250px] sm:h-[300px] md:h-[400px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-4 sm:px-6 lg:px-8" style={{ margin: '10px auto' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 z-0" />
                <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('hero.title')}</h1>
                    <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto opacity-90 px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('hero.subtitle')}</p>
                </div>
            </section>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-0" />

            {/* Error State */}
            {error && renderErrorState()}

            {/* Loading State */}
            {loading && renderLoadingState()}

            {/* Classes List */}
            {!loading && !error && (
                <div className="max-w-6xl mx-auto space-y-12 sm:space-y-16 lg:space-y-20 mb-12 sm:mb-16 lg:mb-20 px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
                    {localizedClasses.length === 0 ? (
                        <div className="text-center py-8 sm:py-12">
                            <p className="text-gray-600 text-base sm:text-lg">{t('noClasses')}</p>
                        </div>
                    ) : (
                        localizedClasses.map((classItem, index) => (
                            <div key={classItem.id} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-6 sm:gap-8 lg:gap-12`}>
                                <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
                                    <h2 className="text-xl sm:text-2xl lg:text-[28px] font-semibold mb-3 sm:mb-4 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>{classItem.title}</h2>
                                    <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base lg:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>{classItem.description}</p>
                                    <div className="mb-6 sm:mb-8 space-y-2">
                                        <p className="text-gray-700 text-sm sm:text-base lg:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            <span className="font-medium">{t('labels.duration')}</span> {getDurationString(classItem)}
                                        </p>
                                        <p className="text-gray-700 text-sm sm:text-base lg:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            <span className="font-medium">{t('labels.location')}</span> {formatClassLocationDisplay(classItem, i18n.language)}
                                        </p>
                                        <p className="text-gray-700 text-sm sm:text-base lg:text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            <span className="font-medium">{t('labels.price')}</span> {formatClassPrice(classItem.price, i18n.language)}
                                        </p>
                                    </div>
                                    <Link
                                        to={`/classes/${classItem.id}`}
                                        className="inline-block bg-black text-white px-6 sm:px-8 py-3 sm:py-4 font-normal border-0 hover:bg-gray-900 transition-colors text-sm sm:text-base"
                                        style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
                                    >
                                        {t('viewDetails')}
                                    </Link>
                                </div>
                                <img
                                    src={getImageSource(classItem)}
                                    alt={classItem.title}
                                    className="flex-1 w-full max-w-[500px] h-[250px] sm:h-[300px] lg:h-[350px] object-cover rounded-lg shadow-md order-1 lg:order-2"
                                    onError={(e) => {
                                        e.target.onerror = null; // Prevent infinite loop
                                        e.target.src = defaultClassImage; // Fallback to default image
                                    }}
                                />
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Call to Action */}
            <section className="bg-gray-100 py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-[34px] font-semibold mb-3 sm:mb-4 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('cta.title')}</h2>
                    <p className="mb-6 sm:mb-8 text-gray-700 text-sm sm:text-base lg:text-lg px-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>{t('cta.description')}</p>
                    <Link
                        to="/contact"
                        className="inline-block bg-black text-white px-6 sm:px-8 py-3 sm:py-4 font-normal border-0 hover:bg-gray-900 transition-colors text-sm sm:text-base"
                        style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
                    >
                        {t('cta.contactUs')}
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
}

export default Classes; 